import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import i18n from 'i18n-js';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { capitalize } from 'utils';
import { Formik, Field } from 'formik';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import useStyles from './BasicInfo.styled';
import { SkusActions } from 'state/modules/skus';
import Container from '@material-ui/core/Container';
import { PricingTypesSelect } from 'components/AsyncFields';
import { TextField, MaterialSelectField } from 'components/FormFields';
import { INPUT_REGEX } from 'constants';

const SkuBasicInfo = ({ formRef, step, accounting, exit }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [priceUnitError, setPriceUnitError] = useState('');

  const initialValues = {
    tax_type: step?.tax_type || '',
    name: step?.name || '',
    description: step?.description || '',
    notes: step?.notes || '',
    price_unit: step?.price_unit || '',
  };

  const formSchema = Yup.object().shape({
    tax_type: Yup.object().required(i18n.t('common.required_field')),
    name: Yup.string().required(i18n.t('common.required_field')),
    description: Yup.string()
      .required(i18n.t('common.required_field'))
      .matches(INPUT_REGEX, i18n.t('common.input_check')),
    notes: Yup.string().max(
      255,
      i18n.t('common.max_long', {
        max_long: 255,
      })
    ),
    price_unit: Yup.string(),
  });

  const onSubmit = (values) => {
    let data = { ...values };
    if (values?.tax_type?.allowed_unit_types?.length && !values?.price_unit) {
      setPriceUnitError(i18n.t('common.required_field'));
      return false;
    }

    setPriceUnitError('');

    // remove empty fields
    Object.keys(data).forEach((key) => !data[key] && delete data[key]);

    dispatch(SkusActions.fetchCreateSku(data, 'details', exit));
  };

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid
          container
          className={classes.container}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            enableReinitialize={true}
            innerRef={formRef}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, values, errors, touched, setFieldValue }) => (
              <form noValidate onSubmit={handleSubmit} className={classes.formContainer}>
                <Grid item xs={12} className={classes.mt}>
                  <Field
                    errors={errors}
                    touched={touched}
                    name="tax_type"
                    multiple={false}
                    margin="none"
                    label={i18n.t('skus.wizard.type')}
                    customParams={{
                      charge_type: ['REVENUE', 'HOLD'].includes(accounting?.accounting_type)
                        ? 'revenue'
                        : 'discount',
                    }}
                    value={values.accounting_type}
                    setFieldValue={setFieldValue}
                    component={PricingTypesSelect}
                  />
                </Grid>
                <Grid item xs={12} className={classes.mt}>
                  <Field
                    name="name"
                    label={i18n.t('common.name')}
                    margin={'none'}
                    component={TextField}
                  />
                </Grid>
                <Grid item xs={12} className={classes.mt}>
                  <Field
                    name="description"
                    label={i18n.t('skus.wizard.invoice_description')}
                    margin={'none'}
                    component={TextField}
                  />
                </Grid>
                <Grid item xs={12} className={classes.mt}>
                  <Field
                    name="notes"
                    label={i18n.t('skus.wizard.invoice_note')}
                    margin={'none'}
                    required={false}
                    component={TextField}
                  />
                </Grid>
                {values?.tax_type?.allowed_unit_types?.length > 0 && (
                  <Grid item xs={6} className={classes.mt}>
                    <Field
                      asyncErrors={priceUnitError}
                      name="price_unit"
                      data-cy="price-unit"
                      component={MaterialSelectField}
                      label={i18n.t('telemetry.units')}
                      options={
                        values?.tax_type?.allowed_unit_types?.map((val) => {
                          return {
                            value: val,
                            label: capitalize(val?.toLowerCase()),
                          };
                        }) || []
                      }
                      placeholder={i18n.t('telemetry.units')}
                    />
                  </Grid>
                )}
              </form>
            )}
          </Formik>
        </Grid>
      </Container>
    </Box>
  );
};

SkuBasicInfo.propTypes = {
  formRef: PropTypes.any,
  step: PropTypes.object,
  accounting: PropTypes.bool,
  exit: PropTypes.func,
};

export default SkuBasicInfo;
